import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { type CreativeTypes } from '@bynder-studio/render-core';
import { getUser } from 'packages/store/user/user.selectors';
import { catchResponseError } from 'packages/helpers/helpers';
import RenditionsService, { RenditionConfiguration } from '~/services/RenditionsService';
import useAccessRights from 'packages/hooks/useAccessRights';
import features from '~/configs/features';

const useRenditionProfiles = ({ creativeType }: { creativeType: CreativeTypes }) => {
    const dispatch = useDispatch();
    const { isPlatformAllowed } = useAccessRights();
    const { currentCompany } = useSelector(getUser);

    const [isLoadingRenditions, setIsLoadingRenditions] = useState(true);
    const [renditionProfiles, setRenditionProfiles] = useState<RenditionConfiguration[]>([]);
    const [selectedProfile, setSelectedProfile] = useState<RenditionConfiguration>(renditionProfiles[0]);

    const deselectProfile = () => {
        setSelectedProfile(renditionProfiles[0]);
    };

    useEffect(() => {
        if (!currentCompany || !['IMAGE', 'VIDEO'].includes(creativeType)) {
            return;
        }

        RenditionsService.getRenditionConfigurations(currentCompany, creativeType as 'IMAGE' | 'VIDEO')
            .then(({ json, status }) => {
                if (status !== 200) {
                    return;
                }

                let renditions = json || [];

                if (!isPlatformAllowed([features.PDF])) {
                    renditions = renditions.filter((rendition) => rendition.type !== 'PDF');
                }

                setRenditionProfiles(renditions);
                setSelectedProfile(renditions[0] || null);
                setIsLoadingRenditions(false);
            })
            .catch(catchResponseError);
    }, [dispatch, currentCompany, creativeType, isPlatformAllowed]);

    return {
        isLoadingRenditions,
        setIsLoadingRenditions,
        renditionProfiles,
        setRenditionProfiles,
        selectedProfile,
        setSelectedProfile,
        deselectProfile,
    };
};

export default useRenditionProfiles;
